import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Camera, Activity, Eye } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import ColumnVisibilityMenu from "../../../components/common/ColumnVisibilityMenu";
import StatusBadge from "../../../components/common/StatusBadge";
import InfoTile from "../../../components/common/InfoTile";
import DateRangePicker, { useDatePresetRange } from "../../../components/common/DateRangePicker";
import ListExportToolbar from "../../stock/components/list/ListExportToolbar";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetFuelCardsQuery, useGetFuelLookupsQuery, useGetFuelVehiclesQuery, useListFuelTransactionsQuery } from "../fuelApi";
import { useCanReadFuelOffice, useCanReviewFuelExceptions, useSeesAllFuelTransactions } from "../useFuelPermissions";
import {
  FUEL_TXN_STATUS_CLASS,
  FUEL_TXN_STATUS_LABEL,
  FUEL_TXN_STATUS_OPTIONS,
  formatDateTime,
  kmSincePrevious,
  litres,
  money,
  whole,
} from "../fuelFormat";
import FuelTransactionDetailModal from "./FuelTransactionDetailModal";
import FuelUserSelect from "./FuelUserSelect";
import MismatchedCardBadge from "./MismatchedCardBadge";
import VehicleTypeSelect from "../../masters/vehicles/components/VehicleTypeSelect";
import { vehicleTypeLabel } from "../../masters/vehicles/vehicleTypes";

// Same approach as VehicleMaintenanceReportPage: one big page from the server,
// paginated/KPI'd/exported client-side, with a banner if it was ever truncated.
const PAGE_SIZE = 2000;

// Tick / dash for one evidence photo.
const EvidenceMark = ({ icon: Icon, present, label }) => (
  <span
    className="d-inline-flex align-items-center me-2"
    style={{ color: present ? "#1E7E34" : "#B0B7C3" }}
    title={`${label}: ${present ? "photo on record" : "no photo"}`}
  >
    <Icon size={15} />
  </span>
);

/**
 * The fuel purchases table with its filters, KPI tiles, export and the detail
 * modal - shared by Fuel Transactions and Fuel Exceptions so the two cannot
 * drift apart. `exceptionsMode` pins exceptionsOnly=true, opens on the
 * "Requires review" status and puts the review action on each row.
 *
 * Read-only by design: a purchase is recorded once, in the mobile app, and is
 * never edited - only reviewed or voided from the detail modal.
 */
const FuelTransactionsView = ({ title, subtitle, exceptionsMode = false }) => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const canReadOffice = useCanReadFuelOffice();
  const canReview = useCanReviewFuelExceptions();
  const seesAll = useSeesAllFuelTransactions();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("LAST_30_DAYS");
  const [vehicleId, setVehicleId] = useState("");
  // Vehicle type (Batch191): filters the purchases server-side and narrows the
  // vehicle picker to that type. Changing type clears a vehicle pick.
  const [vehicleType, setVehicleTypeState] = useState("");
  const setVehicleType = (type) => {
    setVehicleTypeState(type);
    setVehicleId("");
  };
  const [fuelCardId, setFuelCardId] = useState("");
  const [user, setUser] = useState(null); // react-select option { value: userId, label }
  const [fuelType, setFuelType] = useState("");
  const [status, setStatus] = useState(exceptionsMode ? "REQUIRES_REVIEW" : "");
  const [exceptionsOnly, setExceptionsOnly] = useState(false);
  const [mismatchOnly, setMismatchOnly] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [hiddenColumns, setHiddenColumns] = useState([]);
  // ?open=<recordGuid> - a One Stop fuel row lands here with the purchase already open.
  const [searchParams] = useSearchParams();
  const [openGuid, setOpenGuid] = useState(() => searchParams.get("open"));
  const toggleColumn = (key) => setHiddenColumns((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: lookups } = useGetFuelLookupsQuery();
  // The vehicle / card / user pickers are office endpoints - someone who only
  // sees their own purchases gets the list without them rather than a 403.
  const { data: vehicles = [] } = useGetFuelVehiclesQuery(
    { branchId: selectedBranchId ?? undefined, includeInactive: true, vehicleType: vehicleType || undefined },
    { skip: !canReadOffice }
  );
  const { data: cards = [] } = useGetFuelCardsQuery(
    { branchId: selectedBranchId ?? undefined, includeInactive: true },
    { skip: !canReadOffice }
  );

  const { data: page, isLoading, isFetching } = useListFuelTransactionsQuery({
    branchId: selectedBranchId ?? undefined,
    vehicleId: vehicleId ? Number(vehicleId) : undefined,
    vehicleType: vehicleType || undefined,
    fuelCardId: fuelCardId ? Number(fuelCardId) : undefined,
    userId: user?.value ?? undefined,
    fuelType: fuelType || undefined,
    status: status || undefined,
    exceptionsOnly: exceptionsMode || exceptionsOnly || undefined,
    mismatchOnly: mismatchOnly || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    search: search || undefined,
    pageSize: PAGE_SIZE,
  });
  const rows = page?.items || [];
  const totalCount = page?.totalCount ?? rows.length;
  const isTruncated = totalCount > rows.length;

  // Voided purchases stay in the list but never in the money/litre totals -
  // their amount went back to the card.
  const counted = rows.filter((r) => r.status !== "VOIDED");
  const totalLitres = counted.reduce((sum, r) => sum + Number(r.litres || 0), 0);
  const totalAmount = counted.reduce((sum, r) => sum + Number(r.totalAmount || 0), 0);
  const requiringReview = rows.filter((r) => r.status === "REQUIRES_REVIEW").length;
  const mismatched = counted.filter((r) => r.isCardMismatch).length;

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;

  const ALL_COLUMNS = [
    { key: "date", label: "Date / Time", dataIndex: "transactionDate", render: (v) => <span className="text-nowrap">{formatDateTime(v)}</span> },
    {
      key: "vehicle",
      label: "Vehicle",
      dataIndex: "regNo",
      render: (v, r) => (
        <>
          <span className="fw-semibold">{v}</span>
          <div className="small text-muted">{r.transactionNumber} · {r.brand} {r.model}</div>
        </>
      ),
    },
    {
      key: "card",
      label: "Fuel Card",
      dataIndex: "cardNumber",
      render: (v, r) => (
        <>
          {v}
          <div className="small text-muted">{r.provider}</div>
          {r.isCardMismatch && <MismatchedCardBadge expected={r.vehicleDefaultCardNumber} />}
        </>
      ),
    },
    { key: "user", label: "User", dataIndex: "recordedByName", render: (v) => v || "-" },
    { key: "fuelType", label: "Fuel Type", dataIndex: "fuelType", render: (v) => v || "-" },
    { key: "litres", label: "Litres", dataIndex: "litres", align: "right", render: (v) => litres(v) },
    { key: "price", label: "Price/L", dataIndex: "pricePerLitre", align: "right", render: (v) => money(v) },
    { key: "total", label: "Total", dataIndex: "totalAmount", align: "right", render: (v) => <span className="fw-semibold">{money(v)}</span> },
    {
      key: "odometer",
      label: "Odometer",
      dataIndex: "odometerReading",
      align: "right",
      render: (v, r) => {
        const km = kmSincePrevious(r);
        return (
          <>
            {whole(v)}
            {km != null && <div className="small text-muted">+{whole(km)} km</div>}
          </>
        );
      },
    },
    { key: "station", label: "Station", dataIndex: "stationName", render: (v) => v || "-" },
    {
      key: "evidence",
      label: "Evidence",
      dataIndex: "hasOdometerImage",
      render: (_, r) => (
        <span className="text-nowrap">
          <EvidenceMark icon={Activity} present={r.hasOdometerImage} label="Odometer" />
          <EvidenceMark icon={Camera} present={r.hasPumpImage} label="Pump" />
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      dataIndex: "status",
      render: (v, r) => (
        <>
          <StatusBadge status={v} classMap={FUEL_TXN_STATUS_CLASS} labelMap={FUEL_TXN_STATUS_LABEL} />
          {r.exceptionCount > 0 && (
            <div className="small text-muted">{r.exceptionCount} exception{r.exceptionCount === 1 ? "" : "s"}</div>
          )}
        </>
      ),
    },
    ...(exceptionsMode
      ? [
          {
            key: "action",
            label: "Action",
            dataIndex: "recordGuid",
            render: (v, r) => (
              <button
                type="button"
                className={`btn btn-sm ${canReview && r.status === "REQUIRES_REVIEW" ? "btn-primary" : "btn-outline-secondary"} d-inline-flex align-items-center gap-1`}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenGuid(v);
                }}
              >
                <Eye size={14} /> {canReview && r.status === "REQUIRES_REVIEW" ? "Review" : "Open"}
              </button>
            ),
          },
        ]
      : []),
  ];

  const columns = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.key)).map(({ key, label, ...rest }) => ({
    ...rest,
    title: label,
  }));

  const exportColumns = [
    { header: "Date/Time", value: (r) => formatDateTime(r.transactionDate) },
    { header: "No", value: (r) => r.transactionNumber },
    { header: "Vehicle", value: (r) => r.regNo },
    { header: "Card", value: (r) => r.cardNumber },
    { header: "Mismatched Card", value: (r) => (r.isCardMismatch ? `Yes (expected ${r.vehicleDefaultCardNumber || "none"})` : "") },
    { header: "User", value: (r) => r.recordedByName || "" },
    { header: "Fuel", value: (r) => r.fuelType || "" },
    { header: "Litres", value: (r) => litres(r.litres), align: "right" },
    { header: "Price/L", value: (r) => money(r.pricePerLitre), align: "right" },
    { header: "Total", value: (r) => money(r.totalAmount), align: "right" },
    { header: "Odometer", value: (r) => whole(r.odometerReading), align: "right" },
    { header: "Km Since Prev", value: (r) => (kmSincePrevious(r) == null ? "" : whole(kmSincePrevious(r))), align: "right" },
    { header: "Station", value: (r) => r.stationName || "" },
    { header: "Exceptions", value: (r) => r.exceptionCount || "" },
    { header: "Status", value: (r) => FUEL_TXN_STATUS_LABEL[r.status] || r.status },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>{title}</h4>
              <h6>{subtitle}</h6>
            </div>
          </div>
        </div>

        {!seesAll && (
          <div className="alert alert-light border small py-2">
            You are seeing only the fuel purchases you recorded yourself.
          </div>
        )}

        <div className="row g-2 mb-3">
          <div className="col-6 col-md">
            <InfoTile label={exceptionsMode ? "Purchases with exceptions" : "Purchases"}>
              {rows.length}
              {isTruncated && <span className="text-muted ms-1 fw-normal" style={{ fontSize: 11 }}>of {totalCount}</span>}
            </InfoTile>
          </div>
          <div className="col-6 col-md"><InfoTile label="Litres" value={litres(totalLitres)} /></div>
          <div className="col-6 col-md"><InfoTile label="Amount" tone="maroon" value={money(totalAmount)} /></div>
          <div className="col-6 col-md"><InfoTile label="Requiring review" value={requiringReview} /></div>
          <div className="col-6 col-md"><InfoTile label="Mismatched card" value={mismatched} /></div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-start">
                <DateRangePicker preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              </div>
              <div className="table-top flex-wrap gap-2" style={{ rowGap: 8 }}>
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="No., vehicle, card, station"
                      className="form-control form-control-sm formsearch"
                      style={{ minWidth: 200, height: 34 }}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                </div>
                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <VehicleTypeSelect className="form-control form-control-sm" style={{ width: 140, height: 34 }} value={vehicleType} onChange={setVehicleType} allLabel="All Types" />
                  {canReadOffice && (
                    <>
                      <select className="form-control form-control-sm" style={{ width: 180, height: 34 }} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                        <option value="">{vehicleType ? `All ${vehicleTypeLabel(vehicleType)} Vehicles` : "All Vehicles"}</option>
                        {vehicles.map((v) => (
                          <option key={v.vehicleId} value={v.vehicleId}>{v.regNo} - {v.brand} {v.model}</option>
                        ))}
                      </select>
                      <select className="form-control form-control-sm" style={{ width: 180, height: 34 }} value={fuelCardId} onChange={(e) => setFuelCardId(e.target.value)}>
                        <option value="">All Cards</option>
                        {cards.map((c) => (
                          <option key={c.fuelCardId} value={c.fuelCardId}>{c.cardNumber} - {c.provider}</option>
                        ))}
                      </select>
                      {seesAll && (
                        <div style={{ width: 220 }}>
                          <FuelUserSelect value={user} onChange={setUser} placeholder="All Users" compact />
                        </div>
                      )}
                    </>
                  )}
                  <select className="form-control form-control-sm" style={{ width: 130, height: 34 }} value={fuelType} onChange={(e) => setFuelType(e.target.value)}>
                    <option value="">All Fuel Types</option>
                    {(lookups?.fuelTypes || []).map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                  <select className="form-control form-control-sm" style={{ width: 160, height: 34 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                    {FUEL_TXN_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {!exceptionsMode && (
                    <div className="form-check form-switch mb-0">
                      <input type="checkbox" className="form-check-input" id="fuelTxnExceptionsOnly" checked={exceptionsOnly} onChange={(e) => setExceptionsOnly(e.target.checked)} />
                      <label className="form-check-label" htmlFor="fuelTxnExceptionsOnly">Exceptions only</label>
                    </div>
                  )}
                  <div className="form-check form-switch mb-0">
                    <input type="checkbox" className="form-check-input" id={`fuelTxnMismatchOnly${exceptionsMode ? "Ex" : ""}`} checked={mismatchOnly} onChange={(e) => setMismatchOnly(e.target.checked)} />
                    <label className="form-check-label" htmlFor={`fuelTxnMismatchOnly${exceptionsMode ? "Ex" : ""}`}>Mismatched card only</label>
                  </div>
                </div>
                <ColumnVisibilityMenu columns={ALL_COLUMNS} hidden={hiddenColumns} onToggle={toggleColumn} />
                <ListExportToolbar
                  title={title}
                  subtitleLines={[`Branch: ${branchCode || "All"}`, `Vehicle type: ${vehicleType ? vehicleTypeLabel(vehicleType) : "All"}`, `Date: ${dateFrom} to ${dateTo}`]}
                  branchCode={branchCode}
                  columns={exportColumns}
                  rows={rows}
                  fileName={exceptionsMode ? "FuelExceptions" : "FuelTransactions"}
                  orientation="landscape"
                />
              </div>
            </div>

            {isTruncated && (
              <div className="alert alert-warning py-2 px-3 mb-2 mt-2">
                Showing the first {rows.length} of {totalCount} matching purchases - narrow the date range or filters for a complete list.
              </div>
            )}

            <div className="table-responsive mt-2">
              <DataTable
                columns={columns}
                dataSource={rows}
                rowKey="recordGuid"
                loading={isLoading || isFetching}
                onRow={(record) => ({ onClick: () => setOpenGuid(record.recordGuid), style: { cursor: "pointer" } })}
              />
            </div>
          </div>
        </div>
      </div>

      <FuelTransactionDetailModal recordGuid={openGuid} onClose={() => setOpenGuid(null)} />
    </div>
  );
};

export default FuelTransactionsView;
